import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles, Trash2, Star, CheckCircle2, Copy, RefreshCw, Filter,
  Clock, ChevronRight, Package, Hash, Camera, Info, ListChecks,
  TrendingUp, Calendar, Flame, Award
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const BRAND_BROWN = '#5D4E3C';
const BRAND_TAN = '#C4A084';

const FILTERS = [
  { key: 'any', label: 'Alle', icon: Sparkles },
  { key: 'sale', label: 'Sale', icon: Package },
  { key: 'new', label: 'Neuheiten', icon: Sparkles },
  { key: 'pair', label: 'Sets & Kombis', icon: ListChecks },
  { key: 'gift', label: 'Geschenke', icon: Star },
  { key: 'seasonal', label: 'Saisonal', icon: Camera },
];

function IdeaCard({ idea, onDismiss, onSave, onDone, onCopy, disabled }) {
  return (
    <Card
      className="rounded-none border-l-[3px] transition-shadow hover:shadow-md"
      style={{ borderLeftColor: BRAND_TAN, backgroundColor: 'white' }}
      data-testid={`idea-card-${idea.id}`}
    >
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: BRAND_TAN, letterSpacing: '2px' }}>
              {idea.format}
            </div>
            <h3 className="mt-1 font-medium leading-snug" style={{ color: BRAND_BROWN, fontSize: 16 }}>
              {idea.title}
            </h3>
          </div>
          <Badge className="rounded-none shrink-0" style={{ backgroundColor: '#FBF6EE', color: BRAND_BROWN, fontWeight: 400 }}>
            <Clock className="h-3 w-3 mr-1" />
            {idea.effort}
          </Badge>
        </div>

        {/* Product chips */}
        {idea.product_names && idea.product_names.filter(Boolean).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {idea.product_names.filter(Boolean).map((name, i) => (
              <span
                key={i}
                className="text-[11px] px-2 py-0.5 border inline-flex items-center gap-1"
                style={{ borderColor: '#E5D9C8', color: '#6B5D4F' }}
              >
                <Package className="h-2.5 w-2.5" /> {name}
              </span>
            ))}
          </div>
        )}

        {/* Photo setup */}
        <div className="mb-3 p-3" style={{ backgroundColor: '#FBF6EE' }}>
          <div className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: BRAND_BROWN }}>
            <Camera className="h-3 w-3 inline mr-1" />
            Foto-Anleitung
          </div>
          <div className="text-xs leading-relaxed" style={{ color: '#5D4E3C' }}>
            {idea.photo_setup}
          </div>
        </div>

        {/* Caption */}
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: BRAND_BROWN }}>Caption</div>
          <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#4B3F30' }}>
            {idea.caption}
          </div>
        </div>

        {/* Hashtags */}
        {idea.hashtags && idea.hashtags.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {idea.hashtags.map((h, i) => (
              <span key={i} className="text-[11px]" style={{ color: BRAND_TAN }}>
                {h}
              </span>
            ))}
          </div>
        )}

        {/* Hook */}
        {idea.hook && (
          <div className="mb-3 text-xs italic flex gap-1.5" style={{ color: '#8B7355' }}>
            <Info className="h-3 w-3 shrink-0 mt-0.5" />
            <span>{idea.hook}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t" style={{ borderColor: '#F0E6D6' }}>
          {onDismiss && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-none text-xs"
              onClick={() => onDismiss(idea)}
              disabled={disabled}
              data-testid={`idea-dismiss-${idea.id}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Verwerfen
            </Button>
          )}
          {onSave && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-none text-xs"
              style={{ color: '#B7862E' }}
              onClick={() => onSave(idea)}
              disabled={disabled}
              data-testid={`idea-save-${idea.id}`}
            >
              <Star className="h-3.5 w-3.5 mr-1" />
              Merken
            </Button>
          )}
          {onDone && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-none text-xs"
              style={{ color: '#7BA98E' }}
              onClick={() => onDone(idea)}
              disabled={disabled}
              data-testid={`idea-done-${idea.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Erledigt
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="rounded-none text-xs ml-auto"
            onClick={() => onCopy(idea)}
            data-testid={`idea-copy-${idea.id}`}
          >
            <Copy className="h-3.5 w-3.5 mr-1" />
            Kopieren
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ContentCreator() {
  const [ideas, setIdeas] = useState([]);
  const [savedIdeas, setSavedIdeas] = useState([]);
  const [doneIdeas, setDoneIdeas] = useState([]);
  const [doneStats, setDoneStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('any');
  const [howMany, setHowMany] = useState(10);
  const [tab, setTab] = useState('feed');

  const loadIdeas = useCallback(async (count, activeFilter) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/admin/content-creator/generate', {
        params: { count, filter: activeFilter },
      });
      setIdeas(res.data.ideas || []);
      if (res.data.ideas?.length === 0) {
        toast.info(res.data.message || 'Keine neuen Ideen — probiere einen anderen Filter oder markiere gemerkte Ideen als erledigt.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konnte Ideen nicht laden');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSaved = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/content-creator/list', { params: { status: 'saved' } });
      setSavedIdeas(res.data.ideas || []);
    } catch (_e) { /* silent */ }
  }, []);

  const loadDone = useCallback(async () => {
    try {
      const [listRes, statsRes] = await Promise.all([
        axiosInstance.get('/admin/content-creator/list', { params: { status: 'done' } }),
        axiosInstance.get('/admin/content-creator/stats'),
      ]);
      setDoneIdeas(listRes.data.ideas || []);
      setDoneStats(statsRes.data || null);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadIdeas(howMany, filter);
    loadSaved();
    loadDone();
  }, []);

  const replaceIdeaWithNew = async (removedId) => {
    // Fetch 1 fresh idea and swap in
    try {
      const res = await axiosInstance.get('/admin/content-creator/generate', {
        params: { count: 1, filter },
      });
      const fresh = res.data.ideas?.[0];
      setIdeas((prev) => {
        const filtered = prev.filter((i) => i.id !== removedId);
        return fresh ? [...filtered, fresh] : filtered;
      });
    } catch (_e) {
      setIdeas((prev) => prev.filter((i) => i.id !== removedId));
    }
  };

  const dismissIdea = async (idea) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/admin/content-creator/dismiss/${idea.id}`);
      toast.success('Verworfen — neuer Vorschlag geladen.');
      replaceIdeaWithNew(idea.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verwerfen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const saveIdea = async (idea) => {
    setBusy(true);
    try {
      await axiosInstance.post('/admin/content-creator/save', { idea });
      toast.success('Für später gemerkt.');
      setSavedIdeas((prev) => [{ ...idea, status: 'saved' }, ...prev]);
      replaceIdeaWithNew(idea.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const doneIdea = async (idea) => {
    setBusy(true);
    try {
      await axiosInstance.post('/admin/content-creator/done', { idea });
      toast.success('Als erledigt markiert.');
      setDoneIdeas((prev) => [{ ...idea, status: 'done' }, ...prev]);
      setSavedIdeas((prev) => prev.filter((i) => i.id !== idea.id));
      replaceIdeaWithNew(idea.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Konnte nicht als erledigt markieren');
    } finally { setBusy(false); }
  };

  const deleteIdea = async (idea) => {
    try {
      await axiosInstance.delete(`/admin/content-creator/ideas/${idea.id}`);
      toast.success('Endgültig gelöscht.');
      setSavedIdeas((prev) => prev.filter((i) => i.id !== idea.id));
      setDoneIdeas((prev) => prev.filter((i) => i.id !== idea.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const copyIdea = (idea) => {
    const parts = [
      idea.title,
      '',
      idea.caption,
      '',
      (idea.hashtags || []).join(' '),
    ].join('\n');
    navigator.clipboard.writeText(parts).then(
      () => toast.success('Caption + Hashtags kopiert.'),
      () => toast.error('Kopieren fehlgeschlagen'),
    );
  };

  const applyFilter = (k) => {
    setFilter(k);
    loadIdeas(howMany, k);
  };

  const loadMore = () => loadIdeas(howMany, filter);

  return (
    <div className="p-6" data-testid="content-creator-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-light" style={{ color: BRAND_BROWN, letterSpacing: '0.02em' }}>
          Content Creator
        </h1>
        <p className="mt-1 text-sm text-neutral-600 max-w-2xl">
          Ideen für Instagram, Facebook & Werbung — basierend auf deinen echten Produkten. Verwerfen, für später merken oder als erledigt markieren.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="rounded-none">
          <TabsTrigger value="feed" className="rounded-none" data-testid="tab-feed">
            <Sparkles className="h-4 w-4 mr-1" />
            Vorschläge
          </TabsTrigger>
          <TabsTrigger value="saved" className="rounded-none" data-testid="tab-saved">
            <Star className="h-4 w-4 mr-1" />
            Gemerkt ({savedIdeas.length})
          </TabsTrigger>
          <TabsTrigger value="done" className="rounded-none" data-testid="tab-done">
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Erledigt ({doneIdeas.length})
          </TabsTrigger>
        </TabsList>

        {/* FEED TAB */}
        <TabsContent value="feed" className="mt-4">
          {/* Filters + Count */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Filter className="h-4 w-4" style={{ color: BRAND_TAN }} />
            {FILTERS.map((f) => {
              const active = filter === f.key;
              const Ic = f.icon;
              return (
                <button
                  key={f.key}
                  onClick={() => applyFilter(f.key)}
                  className="text-xs px-3 py-1 border transition-colors"
                  style={{
                    borderColor: active ? BRAND_BROWN : '#E5D9C8',
                    backgroundColor: active ? BRAND_BROWN : 'white',
                    color: active ? 'white' : BRAND_BROWN,
                    letterSpacing: '1px',
                  }}
                  data-testid={`filter-${f.key}`}
                >
                  <Ic className="h-3 w-3 inline-block mr-1" />
                  {f.label}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs" style={{ color: '#6B5D4F' }}>Anzahl:</label>
              <select
                value={howMany}
                onChange={(e) => setHowMany(parseInt(e.target.value, 10))}
                className="text-xs border px-2 py-1 rounded-none"
                style={{ borderColor: '#E5D9C8', color: BRAND_BROWN }}
                data-testid="count-selector"
              >
                {[5, 10, 15, 20, 25, 30].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <Button
                size="sm"
                className="rounded-none text-white"
                style={{ backgroundColor: BRAND_BROWN }}
                onClick={loadMore}
                disabled={loading}
                data-testid="regenerate-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Neu laden
              </Button>
            </div>
          </div>

          {/* Ideas grid */}
          {loading && ideas.length === 0 ? (
            <div className="text-sm text-neutral-500 p-8 text-center">Ideen werden generiert…</div>
          ) : ideas.length === 0 ? (
            <div className="text-sm text-neutral-500 p-8 text-center">
              Keine neuen Ideen zu diesem Filter. Wähle &bdquo;Alle&ldquo; oder markiere gemerkte Ideen als erledigt.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {ideas.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  onDismiss={dismissIdea}
                  onSave={saveIdea}
                  onDone={doneIdea}
                  onCopy={copyIdea}
                  disabled={busy}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* SAVED TAB */}
        <TabsContent value="saved" className="mt-4">
          {savedIdeas.length === 0 ? (
            <div className="text-sm text-neutral-500 p-8 text-center">Noch keine gemerkten Ideen.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {savedIdeas.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  onSave={null}
                  onDismiss={(i) => deleteIdea(i)}
                  onDone={doneIdea}
                  onCopy={copyIdea}
                  disabled={busy}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* DONE TAB */}
        <TabsContent value="done" className="mt-4">
          {doneStats && doneStats.total_done > 0 && (
            <div className="mb-6" data-testid="done-stats-panel">
              {/* Big KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-none border-l-[3px] p-4 bg-white" style={{ borderLeftColor: BRAND_TAN }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="h-4 w-4" style={{ color: BRAND_TAN }} />
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Gesamt umgesetzt</span>
                  </div>
                  <div className="text-3xl font-semibold" style={{ color: BRAND_BROWN }} data-testid="stat-total">
                    {doneStats.total_done}
                  </div>
                </div>
                <div className="rounded-none border-l-[3px] p-4 bg-white" style={{ borderLeftColor: '#7BA98E' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="h-4 w-4" style={{ color: '#7BA98E' }} />
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Diesen Monat</span>
                  </div>
                  <div className="text-3xl font-semibold" style={{ color: BRAND_BROWN }} data-testid="stat-this-month">
                    {doneStats.this_month}
                  </div>
                  {doneStats.last_month > 0 && (
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      Vormonat: {doneStats.last_month}
                      {doneStats.this_month >= doneStats.last_month
                        ? ' ▲'
                        : ' ▼'}
                    </div>
                  )}
                </div>
                <div className="rounded-none border-l-[3px] p-4 bg-white" style={{ borderLeftColor: '#D89B7B' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Flame className="h-4 w-4" style={{ color: '#D89B7B' }} />
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Streak</span>
                  </div>
                  <div className="text-3xl font-semibold" style={{ color: BRAND_BROWN }} data-testid="stat-streak">
                    {doneStats.streak_days} <span className="text-sm text-neutral-500">Tage</span>
                  </div>
                </div>
                <div className="rounded-none border-l-[3px] p-4 bg-white" style={{ borderLeftColor: '#B7862E' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4" style={{ color: '#B7862E' }} />
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Aktive Tage</span>
                  </div>
                  <div className="text-3xl font-semibold" style={{ color: BRAND_BROWN }} data-testid="stat-active-days">
                    {doneStats.days_active_total}
                  </div>
                </div>
              </div>

              {/* Format breakdown + top products */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white p-4 border" style={{ borderColor: '#E5D9C8' }}>
                  <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: BRAND_BROWN }}>Nach Format</div>
                  <div className="space-y-2">
                    {doneStats.by_format.map((f) => {
                      const pct = doneStats.total_done ? Math.round((f.count / doneStats.total_done) * 100) : 0;
                      return (
                        <div key={f.format}>
                          <div className="flex justify-between text-xs mb-1" style={{ color: '#5D4E3C' }}>
                            <span>{f.format}</span>
                            <span>{f.count} · {pct} %</span>
                          </div>
                          <div className="h-1.5" style={{ backgroundColor: '#F0E6D6' }}>
                            <div className="h-full" style={{ width: `${pct}%`, backgroundColor: BRAND_TAN }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-white p-4 border" style={{ borderColor: '#E5D9C8' }}>
                  <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: BRAND_BROWN }}>Meist beworbene Produkte</div>
                  {doneStats.top_products.length === 0 ? (
                    <div className="text-xs text-neutral-500">Noch keine Daten.</div>
                  ) : (
                    <div className="space-y-2">
                      {doneStats.top_products.map((p, i) => (
                        <div key={p.id} className="flex items-center justify-between text-xs" style={{ color: '#5D4E3C' }}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-neutral-400 w-4">{i + 1}.</span>
                            <span className="truncate">{p.name}</span>
                          </div>
                          <span className="shrink-0 ml-2">{p.count}×</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Monthly timeline */}
              {doneStats.by_month.length > 1 && (
                <div className="bg-white p-4 border mt-3" style={{ borderColor: '#E5D9C8' }}>
                  <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: BRAND_BROWN }}>Verlauf (letzte 12 Monate)</div>
                  <div className="flex items-end gap-1 h-24">
                    {(() => {
                      const maxCount = Math.max(...doneStats.by_month.map((m) => m.count), 1);
                      return doneStats.by_month.map((m) => (
                        <div key={m.month} className="flex-1 flex flex-col items-center justify-end" title={`${m.month}: ${m.count}`}>
                          <div
                            className="w-full"
                            style={{
                              height: `${(m.count / maxCount) * 100}%`,
                              backgroundColor: BRAND_TAN,
                              minHeight: m.count > 0 ? 3 : 0,
                            }}
                          />
                          <div className="text-[9px] text-neutral-500 mt-1">{m.month.slice(5)}</div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {doneIdeas.length === 0 ? (
            <div className="text-sm text-neutral-500 p-8 text-center">Noch keine Ideen als erledigt markiert.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {doneIdeas.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  onSave={null}
                  onDone={null}
                  onDismiss={(i) => deleteIdea(i)}
                  onCopy={copyIdea}
                  disabled={busy}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
